import React, { useLayoutEffect, useRef, useEffect } from 'react';
import io from "socket.io-client";

const fanParams = new URLSearchParams(window.location.search);

// Ajuste fino del tamano sin redeploy, por si el escalado automatico no cuadra con
// el browser source de OBS: &scale=1.4 (mismo patron que la alerta, ver rewardsHistory.jsx)
const fanScale = (() => {
    const raw = parseFloat(fanParams.get('scale'));
    if (!isFinite(raw) || raw <= 0) return 1;
    return Math.min(3, Math.max(0.5, raw));
})();

// Fan de mentira con el peor caso, para poder afinar el tamano redimensionando la
// ventana sin depender de que haya datos reales en produccion: &preview=1
const previewUser = fanParams.get('preview')
    ? { displayname: 'UsuarioConNombreMuyLargo', todayFanPoints: 12345, faccion: 'Héroe' }
    : null;

// px de referencia para medir: la tinta escala linealmente con el font-size, asi que
// con una sola medicion sale el factor exacto.
const BASE_FONT_SIZE = 100;
// Guarda contra los redondeos a subpixel al pasar de la medida al tamano final.
const SAFETY = 0.98;
// Lo que se pinta por fuera de la tinta y tambien tiene que caber en el marco: la
// mitad exterior del text-stroke (0.02em de los 0.04em) mas el drop-shadow mas ancho,
// que es el de 0.16em de blur (ver App.css).
const DECOR = 0.18;
// Holgura que se le da a la caja por encima de la tinta para que el degradado la
// cubra entera: la mitad del text-stroke que se pinta hacia fuera (0.02em) mas el
// redondeo a subpixel al pasar de la medida al tamano final.
const FILL_PAD = 0.06;

// Heroes Legend dibuja muy por fuera de su caja de layout: las mayusculas suben 1.36em
// sobre la linea base cuando la metrica de la fuente declara 0.84em de ascendente. Con
// getBoundingClientRect solo se ve la caja, asi que ese desborde se quedaba fuera del
// marco y el overflow: hidden del overlay rebanaba el texto por arriba —solo con nombres
// de glifos altos (mayusculas, digitos), de ahi que pasara a ratos. measureText si
// devuelve la tinta real, y sobre ella se calculan tamano y centrado.
const measureCtx = document.createElement('canvas').getContext('2d');

const inkOf = (el, text, fontSize) => {
    if (!measureCtx) return null;
    const cs = window.getComputedStyle(el);
    measureCtx.font = `${cs.fontStyle} ${cs.fontWeight} ${fontSize}px ${cs.fontFamily}`;
    measureCtx.textBaseline = 'alphabetic';
    const m = measureCtx.measureText(text);
    // actualBoundingBox* no existe en los CEF anteriores a Chrome 77 (OBS viejos).
    return typeof m.actualBoundingBoxAscent === 'number' ? m : null;
};

const faccionClass = (faccion) =>
    faccion === 'Héroe' ? 'fanHeroe' : faccion === 'Villano' ? 'fanVillano' : '';

const FanPointsToday = () => {

    const [user, setUser] = React.useState(previewUser || false);
    const boxRef = useRef(null);
    const textRef = useRef(null);

    useEffect(() => {
        const socket = io('https://forallia.com');
        socket.on('fanPointsToday', (arg) => {
            if (arg && arg.todayFanPoints > 0) {
                setUser(arg);
            }
        });
    }, []);

    // El gadget se ve sobre la escena de OBS: html tiene color de fondo propio en
    // App.css y hay que anularlo siempre, tambien sin fan del dia.
    useEffect(() => {
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
    }, []);

    const text = user
        ? `${user.displayname} : ${user.todayFanPoints}`
        : 'No hay Fan del día';

    // Escala por font-size y no por transform: asi el padding, el text-stroke y los
    // drop-shadow (todos en em) crecen con el texto y el degradado se rasteriza
    // nitido en vez de estirarse. El transform solo se usa para colocarlo, no para
    // agrandarlo, asi que no toca la nitidez.
    const fit = React.useCallback(() => {
        const box = boxRef.current;
        const el = textRef.current;
        if (!box || !el) return;
        el.style.transform = '';
        el.style.padding = '';
        el.style.fontSize = BASE_FONT_SIZE + 'px';

        const ink = inkOf(el, el.textContent, BASE_FONT_SIZE);
        if (!ink) {
            // Sin medida de tinta solo queda la caja: se ve como antes, con el texto
            // pegado arriba y el riesgo de que se corte, pero es mejor que no escalar.
            // El padding se queda en el del CSS, que ya va sobrado para que el degradado
            // llegue a los glifos.
            const rect = el.getBoundingClientRect();
            if (rect.width && rect.height) {
                const boxRatio = Math.min(box.clientWidth / rect.width, box.clientHeight / rect.height);
                el.style.fontSize = (BASE_FONT_SIZE * boxRatio * SAFETY * fanScale) + 'px';
            }
            return;
        }

        const decor = 2 * DECOR * BASE_FONT_SIZE;
        const inkWidth = ink.actualBoundingBoxLeft + ink.actualBoundingBoxRight + decor;
        const inkHeight = ink.actualBoundingBoxAscent + ink.actualBoundingBoxDescent + decor;
        if (!inkWidth || !inkHeight) return;
        const ratio = Math.min(box.clientWidth / inkWidth, box.clientHeight / inkHeight);
        const fontSize = BASE_FONT_SIZE * ratio * SAFETY * fanScale;

        // El padding no es decoracion: background-clip: text pinta el degradado solo
        // dentro de la caja de padding y luego lo recorta a la forma de los glifos, asi
        // que lo que sobresale de la caja se queda sin relleno y, con color: transparent,
        // sale invisible —solo el contorno negro. Con Heroes Legend sobresale mucho por
        // arriba (las mayusculas y las tildes suben 1.36em cuando la fuente declara 0.84em
        // de ascendente) y tambien por la derecha, al ser oblicua. La misma caja tiene que
        // dar de si para el text-stroke, que se pinta a caballo del borde de la tinta.
        // Se calcula por medida en vez de a ojo para que valga con cualquier nombre.
        // getComputedStyle devuelve un objeto vivo, asi que el padding del CSS se copia a
        // numeros antes de tocar el del elemento: si no, el orden de las lecturas decidiria
        // si se lee el valor de partida o el ya escrito.
        const base = window.getComputedStyle(el);
        const contentHeight = parseFloat(base.lineHeight);
        const cssPad = [base.paddingTop, base.paddingRight, base.paddingBottom, base.paddingLeft]
            .map((v) => parseFloat(v));
        const halfLeading = (contentHeight - (ink.fontBoundingBoxAscent + ink.fontBoundingBoxDescent)) / 2;
        const baselineFromContentTop = halfLeading + ink.fontBoundingBoxAscent;
        const fillPad = FILL_PAD * BASE_FONT_SIZE;
        // Nunca por debajo del padding del CSS: ese ya esta pensado para el caso normal.
        const overflow = [
            ink.actualBoundingBoxAscent - baselineFromContentTop,
            ink.actualBoundingBoxRight - ink.width,
            ink.actualBoundingBoxDescent - (contentHeight - baselineFromContentTop),
            ink.actualBoundingBoxLeft,
        ];
        el.style.padding = overflow
            .map((side, i) => Math.max(cssPad[i], side + fillPad) / BASE_FONT_SIZE + 'em')
            .join(' ');

        // Centrar la tinta y no la caja, en los dos ejes: ni la linea base ni el origen
        // del texto caen donde sugiere la caja, asi que con el centrado del flex
        // (align-items/justify-content) el texto quedaba alto y corrido a la derecha, y se
        // salia por esos dos lados. En vertical porque la linea base cae mucho mas abajo de
        // lo que dice la caja; en horizontal porque la fuente es oblicua: el ultimo glifo
        // desborda su ancho de avance (actualBoundingBoxRight > width) y el primero arranca
        // a la derecha del origen (actualBoundingBoxLeft negativo), asi que la tinta se va a
        // la derecha —y el padding, que se calcula por desborde real, es asimetrico y la
        // desplaza todavia mas. Todo se mide a BASE_FONT_SIZE y se pasa a em, que escala
        // solo. Se mide despues de fijar el padding, que es justo lo que mueve el origen del
        // texto dentro de la caja y cambia el tamano de esta. Aqui `base` interesa vivo:
        // tiene que devolver el padding recien escrito, no el de partida que se copio en
        // cssPad.
        const rect = el.getBoundingClientRect();
        const baselineFromTop = parseFloat(base.paddingTop) + baselineFromContentTop;
        const inkCenterY = baselineFromTop + (ink.actualBoundingBoxDescent - ink.actualBoundingBoxAscent) / 2;
        // La tinta arranca en el origen del texto (el borde de contenido, o sea
        // padding-left) y se extiende de -actualBoundingBoxLeft a +actualBoundingBoxRight.
        const inkCenterX = parseFloat(base.paddingLeft) + (ink.actualBoundingBoxRight - ink.actualBoundingBoxLeft) / 2;
        const offsetXEm = (rect.width / 2 - inkCenterX) / BASE_FONT_SIZE;
        const offsetYEm = (rect.height / 2 - inkCenterY) / BASE_FONT_SIZE;

        el.style.fontSize = fontSize + 'px';
        el.style.transform = `translate(${offsetXEm * fontSize}px, ${offsetYEm * fontSize}px)`;
    }, []);

    // useLayoutEffect: si se midiera despues del pintado se veria un frame con el
    // texto a 100px desbordando el marco.
    useLayoutEffect(fit, [text, fit]);

    // Heroes Legend es un @font-face con font-display: swap: si se mide antes de que
    // cargue se miden las metricas de Arial y el tamano final sale mal.
    useEffect(() => {
        if (!document.fonts) return;
        let cancelled = false;
        document.fonts.load('1em "Heroes Legend"')
            .then(() => document.fonts.ready)
            .then(() => { if (!cancelled) fit(); })
            .catch(() => { });
        return () => { cancelled = true; };
    }, [fit]);

    // El browser source de OBS se puede redimensionar en caliente.
    useEffect(() => {
        const box = boxRef.current;
        if (!box || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(fit);
        observer.observe(box);
        return () => observer.disconnect();
    }, [fit]);

    return (
        <div className="fanPointsOverlay" ref={boxRef}>
            <h2 ref={textRef} className={'fanPointsLine ' + (user ? faccionClass(user.faccion) : '')}>
                {text}
            </h2>
        </div>
    )
}

export default FanPointsToday;
